import { ApplicationConfig, ErrorHandler, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { MarkedOptions, MarkedRenderer, provideMarkdown, MARKED_OPTIONS } from 'ngx-markdown';
import { provideFireAuthLib } from 'fireauth-lib';
import { provideNgxSkeletonLoader } from 'ngx-skeleton-loader';
import { provideAnalyticsLoggingLib, GlobalErrorHandler } from 'analytics-logging-lib';
import { CMS_POST_API_CONFIG, COMMENTS_API_CONFIG, BOOKMARK_API_CONFIG, POST_REACTION_API_CONFIG } from 'cms-post-lib';
import { USER_MANAGEMENT_API_CONFIG } from 'user-management-lib';
import { authInterceptor } from './shared/interceptors/cmsapi-auth.interceptor';
import { analyticsLoggingInterceptor } from './shared/interceptors/analytics-logging.interceptor';
import { routes } from './app.routes';
import { environment } from '../environments/environment';

export function markedOptionsFactory(): MarkedOptions {
  const renderer = new MarkedRenderer();

  const walkTokens = (token: any) => {
    if (token.type === 'text' && token.text) {
      let modified = false;
      let text = token.text;

      const mentionRegex = /@((\w+)(\s\w+){0,2})/g;
      if (mentionRegex.test(text)) {
        text = text.replace(mentionRegex, '<span class="app-mention">$&</span>');
        modified = true;
      }

      const centerRegex = /^==\s+(.+)$/gm;
      if (centerRegex.test(text)) {
        text = text.replace(centerRegex, '<p class="mkd-p-center">$1</p>');
        modified = true;
      }

      const headingRe = /^(h[1-5])((?:\.\w+)+)\s+(.+)$/;
      const headingMatch = headingRe.exec(text);
      if (headingMatch) {
        const [, depth, classPart, content] = headingMatch;
        const classes = classPart.slice(1).split('.').join(' ');
        text = text.replace(headingRe, `<${depth} class="${classes}">${content}</${depth}>`);
        modified = true;
      }

      const paragraphRe = /^p((?:\.\w+)+)\s+(.+)$/;
      const paragraphMatch = paragraphRe.exec(text);
      if (paragraphMatch) {
        const [, classPart, content] = paragraphMatch;
        const classes = classPart.slice(1).split('.').join(' ');
        text = text.replace(paragraphRe, `<p class="${classes}">${content}</p>`);
        modified = true;
      }

      const highlightRegex = /\|\|([^|]+)\|\|/g;
      if (highlightRegex.test(text)) {
        text = text.replace(highlightRegex, '<span class="mkd-highlight">$1</span>');
        modified = true;
      }

      const dynamicHighlightRegex = /\|([a-zA-Z])\|([^|]+)\|\1\|/g;
      if (dynamicHighlightRegex.test(text)) {
        text = text.replace(dynamicHighlightRegex, '<span class="mkd-highlight-$1">$2</span>');
        modified = true;
      }

      if (modified) {
        token.type = 'html';
        token.raw = token.text;
        token.text = text;
      }
    }
  };

  return { gfm: true, breaks: false, pedantic: false, renderer, walkTokens };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimations(),
    provideClientHydration(withEventReplay()),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withInMemoryScrolling({ anchorScrolling: 'enabled', scrollPositionRestoration: 'enabled' })
    ),
    provideHttpClient(withInterceptors([authInterceptor, analyticsLoggingInterceptor])),
    ...provideFireAuthLib(environment.firebase, environment.useEmulators),
    provideNgxSkeletonLoader({ theme: { extendsFromRoot: true, height: '30px' } }),
    provideAnalyticsLoggingLib({
      apiBaseUrl: environment.cmsApiBaseUrl,
      debug: environment.enableApiDebugLogs,
      autoLogErrors: true,
      enablePerformanceMonitoring: true,
      performanceThreshold: 3000
    }),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    {
      provide: CMS_POST_API_CONFIG,
      useValue: { apiBaseUrl: environment.cmsApiBaseUrl, debug: environment.enableApiDebugLogs }
    },
    {
      provide: COMMENTS_API_CONFIG,
      useValue: { apiBaseUrl: environment.cmsApiBaseUrl, debug: environment.enableApiDebugLogs }
    },
    {
      provide: BOOKMARK_API_CONFIG,
      useValue: { apiBaseUrl: environment.cmsApiBaseUrl, debug: environment.enableApiDebugLogs }
    },
    {
      provide: POST_REACTION_API_CONFIG,
      useValue: { apiBaseUrl: environment.cmsApiBaseUrl, debug: environment.enableApiDebugLogs }
    },
    {
      provide: USER_MANAGEMENT_API_CONFIG,
      useValue: { apiBaseUrl: environment.cmsApiBaseUrl }
    },
    provideMarkdown({
      markedOptions: { provide: MARKED_OPTIONS, useFactory: markedOptionsFactory }
    })
  ]
};
